import { createTheme } from '@mui/material/styles';
import { AppThemeMode } from '../contexts/ThemeModeContext';

export const createAppTheme = (mode: AppThemeMode = 'light') => {
  const isDark = mode === 'dark';

  return createTheme({
  palette: {
    mode,
    primary: {
      main: '#0077CC',
      light: '#3392D6',
      dark: '#005A99',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#006777',
      light: '#00A6C2',
      dark: '#004D5A',
      contrastText: '#ffffff',
    },
    background: {
      default: isDark ? '#07111f' : '#F5F8FC',
      paper: isDark ? '#0d1a2c' : '#ffffff',
    },
    text: {
      primary: isDark ? '#e7eef8' : '#002D5A',
      secondary: isDark ? '#9fb0c7' : '#4B5F73',
      disabled: isDark ? '#64748b' : '#94a5b8',
    },
    divider: isDark ? '#1f3148' : '#D8E2EC',
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    success: {
      main: '#21734B',
      light: '#42d0a5',
      dark: '#0e865f',
    },
    warning: {
      main: '#785600',
      light: '#ffc95d',
      dark: '#b87504',
    },
    error: {
      main: '#B42335',
      light: '#ff7c81',
      dark: '#bf3038',
    },
    info: {
      main: '#003366',
      light: '#68c7ef',
      dark: '#137aa4',
    },
  },
  typography: {
    fontFamily: '"Inter", "Aptos", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    h1: { fontFamily: '"Poppins", "Inter", sans-serif', fontSize: '2rem', fontWeight: 600, letterSpacing: 0, lineHeight: 1.18 },
    h2: { fontFamily: '"Poppins", "Inter", sans-serif', fontSize: '1.45rem', fontWeight: 600, letterSpacing: 0, lineHeight: 1.25 },
    h3: { fontFamily: '"Poppins", "Inter", sans-serif', fontSize: '1.2rem', fontWeight: 600, letterSpacing: 0, lineHeight: 1.35 },
    h4: { fontFamily: '"Poppins", "Inter", sans-serif', fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontFamily: '"Poppins", "Inter", sans-serif', fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontFamily: '"Poppins", "Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.72rem', lineHeight: 1.5, color: '#64748b' },
    overline: { fontSize: '0.63rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' },
    button: { fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.06)',
    '0 1px 4px rgba(0,0,0,0.08)',
    '0 2px 8px rgba(0,0,0,0.08)',
    '0 4px 12px rgba(0,0,0,0.08)',
    '0 4px 16px rgba(0,0,0,0.10)',
    '0 8px 24px rgba(0,0,0,0.10)',
    '0 8px 32px rgba(0,0,0,0.12)',
    '0 12px 40px rgba(0,0,0,0.12)',
    '0 16px 48px rgba(0,0,0,0.14)',
    '0 20px 56px rgba(0,0,0,0.14)',
    '0 24px 64px rgba(0,0,0,0.16)',
    '0 28px 72px rgba(0,0,0,0.16)',
    '0 32px 80px rgba(0,0,0,0.18)',
    '0 36px 88px rgba(0,0,0,0.18)',
    '0 40px 96px rgba(0,0,0,0.20)',
    '0 44px 104px rgba(0,0,0,0.20)',
    '0 48px 112px rgba(0,0,0,0.22)',
    '0 52px 120px rgba(0,0,0,0.22)',
    '0 56px 128px rgba(0,0,0,0.24)',
    '0 60px 136px rgba(0,0,0,0.24)',
    '0 64px 144px rgba(0,0,0,0.26)',
    '0 68px 152px rgba(0,0,0,0.26)',
    '0 72px 160px rgba(0,0,0,0.28)',
    '0 76px 168px rgba(0,0,0,0.28)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { boxSizing: 'border-box' },
        html: { scrollBehavior: 'smooth' },
        body: {
          fontFamily: '"Inter", "Aptos", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
          backgroundColor: isDark ? '#07111f' : '#F5F8FC',
          backgroundImage: isDark ? 'linear-gradient(180deg, #081525 0%, #07111f 100%)' : 'linear-gradient(180deg, #FFFFFF 0%, #EAF5F7 100%)',
          color: isDark ? '#e7eef8' : '#002D5A',
        },
        '::-webkit-scrollbar': { width: '8px', height: '8px' },
        '::-webkit-scrollbar-track': { background: isDark ? '#0b1626' : '#eef6fb' },
        '::-webkit-scrollbar-thumb': { background: isDark ? '#334966' : '#9cb0c9', borderRadius: '8px' },
        '::-webkit-scrollbar-thumb:hover': { background: isDark ? '#466384' : '#738aa8' },
        ...(isDark ? {
          '#root': {
            background: 'linear-gradient(180deg, #081525 0%, #07111f 100%)',
            color: '#e7eef8',
          },
          '[data-mmc-theme="dark"] .MuiPaper-root, [data-mmc-theme="dark"] .MuiCard-root, [data-mmc-theme="dark"] .MuiDrawer-paper, [data-mmc-theme="dark"] .MuiPopover-paper, [data-mmc-theme="dark"] .MuiDialog-paper': {
            backgroundColor: '#0d1a2c !important',
            borderColor: '#1f3148 !important',
            color: '#e7eef8 !important',
            boxShadow: '0 18px 44px rgba(0,0,0,0.38) !important',
          },
          '[data-mmc-theme="dark"] .MuiAppBar-root': {
            backgroundColor: 'rgba(9, 21, 36, 0.9) !important',
            borderColor: '#1f3148 !important',
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .MuiTypography-root': {
            color: '#e7eef8',
          },
          '[data-mmc-theme="dark"] .MuiTypography-colorTextSecondary, [data-mmc-theme="dark"] .MuiTypography-caption': {
            color: '#9fb0c7 !important',
          },
          '[data-mmc-theme="dark"] .MuiInputBase-root, [data-mmc-theme="dark"] .MuiOutlinedInput-root': {
            backgroundColor: '#0a1626 !important',
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .MuiOutlinedInput-notchedOutline': {
            borderColor: '#334966 !important',
          },
          '[data-mmc-theme="dark"] .MuiInputLabel-root, [data-mmc-theme="dark"] .MuiFormLabel-root': {
            color: '#9fb0c7 !important',
          },
          '[data-mmc-theme="dark"] input, [data-mmc-theme="dark"] textarea, [data-mmc-theme="dark"] select': {
            backgroundColor: '#0a1626 !important',
            color: '#e7eef8 !important',
            borderColor: '#334966 !important',
          },
          '[data-mmc-theme="dark"] input::placeholder, [data-mmc-theme="dark"] textarea::placeholder': {
            color: '#7e90aa !important',
            opacity: 1,
          },
          '[data-mmc-theme="dark"] .MuiTableCell-root': {
            borderColor: '#1f3148 !important',
            color: '#d8e4f2 !important',
          },
          '[data-mmc-theme="dark"] .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: '#0a1626 !important',
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .MuiListItemButton-root:not(.Mui-selected):hover, [data-mmc-theme="dark"] .MuiMenuItem-root:hover': {
            backgroundColor: '#13243a !important',
          },
          '[data-mmc-theme="dark"] .MuiListItemButton-root.Mui-selected': {
            backgroundColor: '#0077CC !important',
            color: '#ffffff !important',
          },
          '[data-mmc-theme="dark"] .MuiChip-root:not(.MuiChip-colorPrimary):not(.MuiChip-colorSecondary):not(.MuiChip-colorSuccess):not(.MuiChip-colorError):not(.MuiChip-colorWarning):not(.MuiChip-colorInfo)': {
            backgroundColor: '#13243a !important',
            borderColor: '#334966 !important',
            color: '#d8e4f2 !important',
          },
          '[data-mmc-theme="dark"] .MuiButton-outlined': {
            backgroundColor: '#0a1626',
            borderColor: '#334966 !important',
          },
          '[data-mmc-theme="dark"] .MuiDivider-root': {
            borderColor: '#1f3148 !important',
          },
          '[data-mmc-theme="dark"] .ant-card, [data-mmc-theme="dark"] .ant-modal-content, [data-mmc-theme="dark"] .ant-popover-inner, [data-mmc-theme="dark"] .ant-select-selector, [data-mmc-theme="dark"] .ant-input, [data-mmc-theme="dark"] .ant-input-password': {
            background: '#0d1a2c !important',
            borderColor: '#1f3148 !important',
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .ant-typography, [data-mmc-theme="dark"] .ant-form-item-label > label': {
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .ant-btn-default, [data-mmc-theme="dark"] .mmc-export-button': {
            background: '#0a1626 !important',
            borderColor: '#334966 !important',
            color: '#d8e4f2 !important',
          },
          '[data-mmc-theme="dark"] .ant-btn-default:hover, [data-mmc-theme="dark"] .mmc-export-button:hover': {
            background: '#13243a !important',
            borderColor: '#3392D6 !important',
            color: '#ffffff !important',
          },
          '[data-mmc-theme="dark"] .ant-dropdown-menu': {
            background: '#0d1a2c !important',
            border: '1px solid #1f3148 !important',
          },
          '[data-mmc-theme="dark"] .ant-dropdown-menu-item': {
            color: '#d8e4f2 !important',
          },
          '[data-mmc-theme="dark"] .ant-dropdown-menu-item:hover': {
            background: '#13243a !important',
          },
          '[data-mmc-theme="dark"] .ant-table-wrapper, [data-mmc-theme="dark"] .ant-table, [data-mmc-theme="dark"] .ant-table-container, [data-mmc-theme="dark"] .ant-table-content, [data-mmc-theme="dark"] .ant-table-body': {
            background: '#0d1a2c !important',
            color: '#d8e4f2 !important',
          },
          '[data-mmc-theme="dark"] .ant-table-thead > tr > th': {
            background: '#0a1626 !important',
            borderColor: '#1f3148 !important',
            color: '#9fb0c7 !important',
          },
          '[data-mmc-theme="dark"] .ant-table-tbody > tr > td': {
            background: '#0d1a2c !important',
            borderColor: '#1f3148 !important',
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .ant-table-tbody > tr:hover > td': {
            background: '#13243a !important',
          },
          '[data-mmc-theme="dark"] .ant-table-placeholder .ant-table-cell': {
            background: '#0d1a2c !important',
            color: '#9fb0c7 !important',
          },
          '[data-mmc-theme="dark"] .ant-pagination-item, [data-mmc-theme="dark"] .ant-pagination-prev .ant-pagination-item-link, [data-mmc-theme="dark"] .ant-pagination-next .ant-pagination-item-link, [data-mmc-theme="dark"] .ant-select-selector': {
            background: '#0a1626 !important',
            borderColor: '#334966 !important',
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .ant-pagination-item a, [data-mmc-theme="dark"] .ant-select-selection-item, [data-mmc-theme="dark"] .ant-select-selection-placeholder': {
            color: '#e7eef8 !important',
            opacity: '1 !important',
          },
          '[data-mmc-theme="dark"] .ant-select-arrow': {
            color: '#9fb0c7 !important',
          },
          '[data-mmc-theme="dark"] .mmc-context-select .ant-select-selection-item, [data-mmc-theme="dark"] .mmc-context-select .ant-select-selection-placeholder, [data-mmc-theme="dark"] .mmc-context-select .ant-select-selection-search-input': {
            color: '#e7eef8 !important',
            opacity: '1 !important',
          },
          '[data-mmc-theme="dark"] .mmc-context-select .ant-select-arrow': {
            color: '#9fb0c7 !important',
          },
          '[data-mmc-theme="dark"] .ant-pagination-item-active': {
            borderColor: '#3392D6 !important',
            background: '#102642 !important',
          },
          '[data-mmc-theme="dark"] .ant-table-cell .ant-typography, [data-mmc-theme="dark"] .ant-card .ant-typography': {
            color: '#e7eef8 !important',
          },
          '[data-mmc-theme="dark"] .ant-table button': {
            color: '#9fb0c7 !important',
          },
        } : {}),
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, size: 'small' },
      styleOverrides: {
        root: {
          borderRadius: 7,
          fontWeight: 600,
          fontSize: '0.8125rem',
          textTransform: 'none',
          minHeight: 36,
          padding: '0 14px',
          transition: 'all 0.15s ease',
          whiteSpace: 'nowrap',
          '& .MuiButton-startIcon': {
            marginLeft: -2,
            marginRight: 6,
            '& > *:nth-of-type(1)': { fontSize: 18 },
          },
          '& .MuiButton-endIcon': {
            marginLeft: 6,
            marginRight: -2,
            '& > *:nth-of-type(1)': { fontSize: 18 },
          },
        },
        sizeSmall: { minHeight: 32, padding: '0 12px', fontSize: '0.8125rem' },
        sizeMedium: { minHeight: 36, padding: '0 14px', fontSize: '0.8125rem' },
        sizeLarge: { minHeight: 40, padding: '0 18px', fontSize: '0.875rem' },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: '#0077CC',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#005A99' },
          },
        },
        {
          props: { variant: 'outlined', color: 'inherit' },
          style: {
            borderColor: '#d0d0d0',
            color: '#555555',
            '&:hover': { borderColor: '#9e9e9e', backgroundColor: '#f5f5f5' },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            borderColor: 'rgba(0,103,119,0.36)',
            color: '#006777',
            '&:hover': { borderColor: '#006777', backgroundColor: '#EAF5F7' },
          },
        },
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            color: '#0077CC',
            '&:hover': { backgroundColor: '#EAF5F7' },
          },
        },
      ],
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 7,
            backgroundColor: '#ffffff',
            '& fieldset': { borderColor: '#D8E2EC' },
            '&:hover fieldset': { borderColor: '#9fb0c8' },
            '&.Mui-focused fieldset': { borderColor: '#0077CC', borderWidth: 2 },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: '#0077CC' },
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          borderRadius: 7,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D8E2EC' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9fb0c8' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0077CC', borderWidth: 2 },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 7,
          border: '1px solid #D8E2EC',
          backgroundColor: '#ffffff',
          boxShadow: '0 8px 22px rgba(18, 38, 63, 0.04)',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 7,
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: '#D8E2EC',
          boxShadow: '0 8px 22px rgba(18, 38, 63, 0.04)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 500,
          fontSize: '0.68rem',
          height: 24,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#EAF5F7',
            fontWeight: 600,
            fontSize: '0.68rem',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: '#4B5F73',
            borderBottom: '1px solid #D8E2EC',
            padding: '10px 16px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #edf3f8',
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: '#f7fbff' },
          '&:last-child td': { borderBottom: 'none' },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 44,
          '&.Mui-selected': { fontWeight: 700, color: '#0077CC' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#0077CC', height: 2 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 8, border: '1px solid #D8E2EC' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1rem', fontWeight: 600, padding: '20px 24px 12px' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
          tooltip: {
            backgroundColor: '#002D5A',
          fontSize: '0.75rem',
          borderRadius: 4,
          padding: '6px 10px',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 2, backgroundColor: '#e0e0e0' },
        bar: { borderRadius: 2 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 4, fontSize: '0.875rem' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#D8E2EC' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          '&.Mui-selected': {
            backgroundColor: '#0077CC',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#005A99' },
          },
          '&:hover': { backgroundColor: '#EAF5F7' },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: '0.8125rem' },
        separator: { color: '#9e9e9e' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&:hover': { backgroundColor: '#EAF5F7' },
        },
        sizeSmall: {
          width: 32,
          height: 32,
          padding: 0,
          '& .MuiSvgIcon-root': { fontSize: 18 },
        },
        sizeMedium: {
          width: 36,
          height: 36,
          padding: 0,
          '& .MuiSvgIcon-root': { fontSize: 20 },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: '#0077CC' },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3392D6' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#9e9e9e',
          '&.Mui-checked': { color: '#0077CC' },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#9e9e9e',
          '&.Mui-checked': { color: '#0077CC' },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: '#424242',
          '&.Mui-focused': { color: '#0077CC' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&.Mui-focused': { color: '#0077CC' },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&:hover': { backgroundColor: '#EAF5F7' },
          '&.Mui-selected': { backgroundColor: '#EAF5F7', '&:hover': { backgroundColor: '#D8EDF1' } },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { borderRadius: 8, border: '1px solid #D8E2EC', boxShadow: '0 14px 32px rgba(18,38,63,0.12)' },
        option: {
          fontSize: '0.875rem',
          '&:hover': { backgroundColor: '#EAF5F7' },
          '&[aria-selected="true"]': { backgroundColor: '#EAF5F7' },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: { '& .MuiPaginationItem-root': { borderRadius: 8 } },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontSize: '0.6875rem', fontWeight: 700, minWidth: 18, height: 18 },
      },
    },
  },
});
};

const theme = createAppTheme('light');
export default theme;
